import { get_publicUserTags } from "@/api/uaapi/user";

/** 功能标签 code（与 /public/user-tags 的 data[].code、getUserInfo.tags 对齐） */
export const USER_TAG_GRAY_FEATURES = {
  SPIKEX_CARD: "spikexCard",
  TURNTABLE: "turntable",
};

/** public/user-tags 响应：{ code:200, data:[{ code, name }] }；XtAxios 解包后通常为 data 数组 */
const PUBLIC_TAG_CODE_KEYS = ["code", "tag", "tagName", "key"];
const USER_TAG_CODE_KEYS = ["code", "tag", "tagName", "key"];

function normalizeTagName(value) {
  if (value == null) return "";
  return String(value).trim().toLowerCase();
}

function readCodeFromObject(item, keys) {
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    if (item[key] != null && String(item[key]).trim() !== "") {
      return normalizeTagName(item[key]);
    }
  }
  return "";
}

/** public/user-tags 单项：取 code，勿用 name（仅为展示文案） */
export function readPublicTagCode(item) {
  if (item == null) return "";
  if (typeof item === "string" || typeof item === "number") {
    return normalizeTagName(item);
  }
  if (typeof item === "object") {
    return readCodeFromObject(item, PUBLIC_TAG_CODE_KEYS);
  }
  return "";
}

/** getUserInfo.tags 单项（如 "BoZhu" 或与 public 相同的 code） */
export function readUserTagCode(item) {
  if (item == null) return "";
  if (typeof item === "string" || typeof item === "number") {
    return normalizeTagName(item);
  }
  if (typeof item === "object") {
    return readCodeFromObject(item, USER_TAG_CODE_KEYS);
  }
  return "";
}

/**
 * 解析 /public/user-tags 响应为标签名数组（allTags）
 * @returns {string[]}
 */
export function normalizeAllTags(response) {
  let list = response;
  if (response && typeof response === "object" && !Array.isArray(response)) {
    list = response.tags ?? response.list ?? response.items ?? response.result ?? response.data;
  }
  if (!Array.isArray(list)) return [];

  const seen = new Set();
  const allTags = [];
  list.forEach((item) => {
    const tag = readPublicTagCode(item);
    if (!tag || seen.has(tag)) return;
    seen.add(tag);
    allTags.push(tag);
  });
  return allTags;
}

/** @deprecated 使用 normalizeAllTags */
export function normalizePublicUserTagList(response) {
  return normalizeAllTags(response);
}

/**
 * 从 getUserInfo 返回中提取用户标签
 * @param {object|null|undefined} userInfo
 * @returns {string[]}
 */
export function normalizeUserTags(userInfo) {
  const tags = userInfo?.tags;
  if (!Array.isArray(tags)) return [];
  return tags.map((item) => readUserTagCode(item)).filter(Boolean);
}

export function hasUserTag(userTags, featureTag) {
  const target = normalizeTagName(featureTag);
  if (!target) return false;
  return (userTags || []).some((tag) => tag === target);
}

export function isTagInAllTags(allTags, featureTag) {
  const target = normalizeTagName(featureTag);
  if (!target) return false;
  return (allTags || []).includes(target);
}

/**
 * 同步判定某功能是否对用户放行
 *
 * 规则（与后端约定一致）：
 * - 标签在 allTags 中：仅 userTags 也含该标签的用户可见（灰度中）
 * - 标签不在 allTags 中：视为该功能已全量开放（标签被删 = 推全）
 * - tagsLoaded=false：尚未拉到 allTags，默认不放行，避免加载前误开
 *
 * 等价写法：
 *   const hasGrayTag = allTags.includes(tag) && userTags.includes(tag);
 *   return hasGrayTag || !allTags.includes(tag);
 *
 * @param {{
 *   featureTag: string,
 *   allTags?: string[],
 *   userTags?: string[],
 *   tagsLoaded?: boolean,
 * }} params
 */
export function isUserTagFeatureEnabled({
  featureTag,
  allTags = [],
  userTags = [],
  tagsLoaded = true,
}) {
  if (!tagsLoaded) return false;

  const tag = normalizeTagName(featureTag);
  if (!tag) return false;

  const inAllTags = isTagInAllTags(allTags, tag);
  if (!inAllTags) return true;

  return hasUserTag(userTags, tag);
}

let cachedAllTags = null;
let cachedLoaded = false;
let fetchPromise = null;

export function getCachedAllTags() {
  return {
    allTags: cachedAllTags || [],
    loaded: cachedLoaded,
  };
}

/**
 * 拉取全站标签列表（同会话内缓存；首页 bootstrap 时调用）
 * @param {{ forceRefresh?: boolean, config?: object }} [options]
 * @returns {Promise<string[]>}
 */
export async function fetchPublicUserTags(options = {}) {
  const { forceRefresh = false, config } = options;
  if (!forceRefresh && cachedLoaded) {
    return cachedAllTags || [];
  }
  if (!forceRefresh && fetchPromise) {
    return fetchPromise;
  }

  fetchPromise = get_publicUserTags(config)
    .then((response) => {
      cachedAllTags = normalizeAllTags(response);
      cachedLoaded = true;
      return cachedAllTags;
    })
    .catch(() => {
      cachedAllTags = [];
      cachedLoaded = true;
      return cachedAllTags;
    })
    .finally(() => {
      fetchPromise = null;
    });

  return fetchPromise;
}

export function clearPublicUserTagsCache() {
  cachedAllTags = null;
  cachedLoaded = false;
  fetchPromise = null;
}

/**
 * 异步判定（若尚未 bootstrap，会先拉 public/user-tags）
 */
export async function checkUserTagFeatureEnabled(featureTag, options = {}) {
  const { userInfo, tagsLoaded, forceRefresh, config } = options;
  let allTags = cachedAllTags;
  let loaded = cachedLoaded;

  if (forceRefresh || !loaded) {
    allTags = await fetchPublicUserTags({ forceRefresh, config });
    loaded = true;
  }

  return isUserTagFeatureEnabled({
    featureTag,
    allTags: allTags || [],
    userTags: normalizeUserTags(userInfo),
    tagsLoaded: tagsLoaded ?? loaded,
  });
}
